import React, { useEffect, useMemo } from 'react';
import { Page } from '../../layout';
import DownloadReportModal from './DownloadReportModal';
import { useQuery, useReactiveVar } from '@apollo/client';
import { userPrivileges, userTier } from '../../../apollo/cache';
import ResponsiveTabMenu from '../../shared/components/ResponsiveTabMenu';
import {
  ORG_REPORT_ACTUAL_TYPE,
  ORG_REPORT_AGGREGATE_TYPE,
  ORG_REPORT_DELTA_TYPE,
  ORG_REPORT_DETAILED_TYPE
} from '../organizationReportsCache';
import { Navigate, useNavigate } from 'react-router-dom';
import DeltaPerformanceReport from './DeltaPerformanceReport';
import ActualPerformanceReport from './ActualPerformanceReport';
import AggregateAppraisalReport from './AggregateAppraisalReport';
import DetailedAppraisalReport from './detailedAppraislReport/DetailedAppraisalReport';
import ApolloError from '../../shared/components/ApolloError';
import useUriFragment from './../../shared/hooks/useUriFragment';
import PrivilegeNames from '../../../constants/privilegeNames';
import { isUserFDATier } from '../../shared/services/checkUserTierService';
import GET_SHARED_REPORTS_WITH_FDA from '../queries/getSharedReportsWithFdaQuery';
import { getUnitSearchForOrgReports } from '../../shared/services/getUnitSearchPageUrlService';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { getActiveTabDisplayName } from '../../shared/services/getActiveTabDisplayNameService';
import {
  canUserAccessOrgUnitName,
  canUserAccessSeveralOrganization
} from '../../shared/services/userAccessibilityService';
import { getPageTitle } from '../../shared/services/pageTitleService';
import useOrgUnitIdFromURL from '../../shared/hooks/useOrgUnitIdFromURL';
import GET_ORG_UNIT_INFO from '../../shared/queries/getOrgUnitInfoQuery';
import { getDataNotFoundPage } from '../../shared/services/getDataNotFoundPageService';
import OrgUnitBreadcrumbAnnotation from '../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';

const validTabs = [
  { value: ORG_REPORT_DETAILED_TYPE },
  { value: ORG_REPORT_AGGREGATE_TYPE },
  { value: ORG_REPORT_ACTUAL_TYPE },
  { value: ORG_REPORT_DELTA_TYPE }
];

const OrganizationReportsPage = () => {
  const orgUnitId = useOrgUnitIdFromURL();

  const privileges = useReactiveVar(userPrivileges);
  const tier = useReactiveVar(userTier);

  const navigate = useNavigate();

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const {
    data: sharedReportsWithFdaResult,
    error: sharedReportsWithFdaError,
    loading: sharedReportsWithFdaLoading
  } = useQuery(GET_SHARED_REPORTS_WITH_FDA, {
    skip: !isUserFDATier(tier),
    variables: { id: orgUnitId }
  });

  const sharedReportsWithFda = sharedReportsWithFdaResult?.mddapOrgUnitSharedReportWithFda ?? null;
  const tabs = useMemo(
    () => getTabsForUser(privileges, sharedReportsWithFda, tier),
    [privileges, sharedReportsWithFda, tier]
  );

  const [activeTab, setActiveTab, reactiveTab] = useUriFragment(tabs);
  const activeTabDisplayName = getActiveTabDisplayName(tabs, activeTab);

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;

  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([
    canUserAccessOrgUnitName(tier) ? orgUnitName : '',
    activeTabDisplayName
  ]);
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, renderedOrgUnit]
    : [renderedOrgUnit];
  const breadcrumbPathItems = [
    { name: 'Data Reports', url: '/data-reports' },
    { name: 'Organization Reports', url: getUnitSearchForOrgReports(privileges, orgUnitId) },
    { name: activeTabDisplayName, url: '' }
  ];

  useEffect(() => {
    if (!sharedReportsWithFda) {
      return;
    }

    if (!activeTab && !sharedReportsWithFda.hasSharedDetailedAppraisalData) {
      reactiveTab(ORG_REPORT_AGGREGATE_TYPE);
    }
    if (!activeTab && sharedReportsWithFda.hasSharedDetailedAppraisalData) {
      reactiveTab(ORG_REPORT_DETAILED_TYPE);
    }
  }, [sharedReportsWithFda, activeTab, reactiveTab]);

  useEffect(() => {
    if (!activeTab || tabs.length === 0) return;

    const isTabExist = validTabs.find(x => x.value === activeTab);
    const isTabAuthorized = tabs.find(x => x.value === activeTab);

    if (!isTabExist) {
      navigate('/page-not-found');
      return;
    }

    if (!isTabAuthorized) {
      navigate('/permissions');
    }
  }, [tabs, activeTab, navigate]);

  const handleReportChange = type => {
    setActiveTab(type);
  };

  const reportElement = createReport(orgUnitId, activeTab, tier, sharedReportsWithFdaLoading);

  if (orgUnitData && orgUnitData.mddapOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (sharedReportsWithFdaError || orgUnitError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='org-reports'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}>
        <DownloadReportModal
          hasSharedDetailedAppraisalData={
            sharedReportsWithFda ? sharedReportsWithFda.hasSharedDetailedAppraisalData : false
          }
          hasSharedPerformanceData={
            sharedReportsWithFda ? sharedReportsWithFda.hasSharedPerformanceData : false
          }
          orgUnitId={orgUnitId}
        />
      </Breadcrumb>

      <ResponsiveTabMenu
        activeTab={activeTab}
        tabs={tabs}
        onChange={newType => handleReportChange(newType)}
        loading={sharedReportsWithFdaLoading}
      />
      <div>{reportElement}</div>
    </Page>
  );
};

const getTabsForUser = (privileges, sharedReportsWithFda, tier) => {
  if (!privileges || !privileges.length || (sharedReportsWithFda === null && isUserFDATier(tier)))
    return [];

  const tabs = [];

  if (
    privileges.includes(PrivilegeNames.DetailedAppraisalReport) ||
    (isUserFDATier(tier) && sharedReportsWithFda.hasSharedDetailedAppraisalData)
  ) {
    tabs.push({
      value: ORG_REPORT_DETAILED_TYPE,
      displayName: 'Detailed Appraisal Report'
    });
  }

  if (privileges.includes(PrivilegeNames.AggregateAppraisalReport)) {
    tabs.push({
      value: ORG_REPORT_AGGREGATE_TYPE,
      displayName: 'Aggregate Appraisal Report'
    });
  }

  if (
    privileges.includes(PrivilegeNames.ActualPerformanceReport) ||
    (isUserFDATier(tier) && sharedReportsWithFda.hasSharedPerformanceData)
  ) {
    tabs.push({
      value: ORG_REPORT_ACTUAL_TYPE,
      displayName: 'Actual Performance Report'
    });
  }

  if (privileges.includes(PrivilegeNames.DeltaPerformanceReport)) {
    tabs.push({
      value: ORG_REPORT_DELTA_TYPE,
      displayName: 'Delta Performance Report'
    });
  }

  return tabs;
};

const createReport = (orgUnitId, reportType, tier, sharedReportsWithFdaLoading) => {
  if (sharedReportsWithFdaLoading) {
    return null;
  }

  switch (reportType) {
    case ORG_REPORT_AGGREGATE_TYPE:
      return <AggregateAppraisalReport orgUnitId={orgUnitId} tier={tier} />;
    case ORG_REPORT_DETAILED_TYPE:
      return <DetailedAppraisalReport orgUnitId={orgUnitId} tier={tier} />;
    case ORG_REPORT_ACTUAL_TYPE:
      return <ActualPerformanceReport orgUnitId={orgUnitId} tier={tier} />;
    case ORG_REPORT_DELTA_TYPE:
      return <DeltaPerformanceReport orgUnitId={orgUnitId} tier={tier} />;
    default:
      return '';
  }
};

export default OrganizationReportsPage;
